import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { OpsReportData, CountEntry } from './opsReportData';
import { breakdownRows, perfectScans } from './exportWeeklyReport';
import betanxtWhiteLogo from '../assets/betanxt-white-logo.png';

const MARGIN = 14;

const NAVY: [number, number, number] = [4, 50, 67];     // #043243 — real app topbar (AppShell.tsx)
const TEAL: [number, number, number] = [45, 117, 117];  // #2D7575 brand-primary
const INK: [number, number, number] = [15, 23, 42];
const COPY: [number, number, number] = [71, 85, 105];
const MUTED: [number, number, number] = [148, 163, 184];
const TRIM: [number, number, number] = [229, 231, 235];
const HIGH: [number, number, number] = [34, 197, 94];   // #22c55e
const MID: [number, number, number] = [245, 158, 11];   // #f59e0b
const LOW: [number, number, number] = [239, 68, 68];    // #ef4444
const DANGER_TEXT: [number, number, number] = [185, 28, 28];      // #b91c1c
const OUTSTANDING_TEXT: [number, number, number] = [146, 64, 14]; // #92400E

// Mirrors accuracyColor() in ExtractionAccuracyPage.tsx (100 / >=80 thresholds).
function categoryAccuracyColor(pct: number): [number, number, number] {
  if (pct === 100) return HIGH;
  if (pct >= 80) return MID;
  return LOW;
}

// Mirrors fieldBarColor() in ExtractionAccuracyPage.tsx (>=95 / >=80 thresholds).
function fieldAccuracyColor(pct: number): [number, number, number] {
  if (pct >= 95) return HIGH;
  if (pct >= 80) return MID;
  return LOW;
}

function finalY(doc: jsPDF): number {
  return (doc as unknown as { lastAutoTable: { finalY: number } }).lastAutoTable.finalY;
}

function addSectionTitle(doc: jsPDF, text: string, y: number): number {
  doc.setFontSize(13);
  doc.setTextColor(...INK);
  doc.text(text, MARGIN, y);
  return y + 5;
}

function addSubtitle(doc: jsPDF, text: string, y: number): number {
  doc.setFontSize(9);
  doc.setTextColor(...MUTED);
  doc.text(text, MARGIN, y);
  doc.setTextColor(...INK);
  return y + 6;
}

function addTable(doc: jsPDF, y: number, head: string[], body: string[][], didParseCell?: (data: any) => void): number {
  autoTable(doc, {
    startY: y,
    head: [head],
    body,
    margin: { left: MARGIN, right: MARGIN },
    styles: { fontSize: 9, textColor: COPY },
    headStyles: { fillColor: TEAL, textColor: [255, 255, 255] },
    didParseCell,
  });
  return finalY(doc) + 8;
}

function addCountTable(doc: jsPDF, y: number, title: string, colLabel: string, entries: CountEntry[], didParseCell?: (data: any) => void): number {
  if (entries.length === 0) return y;
  y = addSectionTitle(doc, title, y);
  return addTable(doc, y, [colLabel, 'Count'], entries.map(e => [e.label, String(e.count)]), didParseCell);
}

function preloadLogo(): Promise<HTMLImageElement | null> {
  return new Promise(resolve => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = betanxtWhiteLogo;
  });
}

async function addCoverPage(doc: jsPDF, period: string, isMock?: boolean): Promise<void> {
  const pageWidth = doc.internal.pageSize.getWidth();

  doc.setFillColor(...NAVY);
  doc.rect(0, 0, pageWidth, 55, 'F');

  const logo = await preloadLogo();
  if (logo) {
    doc.addImage(logo, 'PNG', MARGIN, 16, 50, 12);
  } else {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text('BetaNXT Ace', MARGIN, 24);
    doc.setFont('helvetica', 'normal');
  }

  let y = 75;
  doc.setFontSize(19);
  doc.setTextColor(...INK);
  doc.text('AI Extraction Accuracy — Compliance Report', MARGIN, y);
  y += 8;
  doc.setFontSize(11);
  doc.setTextColor(...TEAL);
  doc.text('COMPLIANCE & AUDIT DOCUMENTATION', MARGIN, y);
  y += 16;

  const meta: [string, string][] = [
    ['Period Covered', period],
    ['Generated On', new Date().toLocaleString()],
    ['Data Classification', 'Internal'],
    ['Confidentiality', 'Confidential — Internal Distribution Only'],
  ];
  doc.setFontSize(10);
  for (const [label, value] of meta) {
    doc.setTextColor(...MUTED);
    doc.text(label, MARGIN, y);
    doc.setTextColor(...INK);
    doc.text(value, MARGIN + 45, y);
    y += 7;
  }

  if (isMock) {
    y += 6;
    doc.setFillColor(254, 243, 199);
    doc.rect(MARGIN, y - 5, doc.internal.pageSize.getWidth() - MARGIN * 2, 12, 'F');
    doc.setFontSize(9);
    doc.setTextColor(146, 64, 14);
    doc.text('SAMPLE DATA — no extraction activity recorded for this period; figures are illustrative, not measured.', MARGIN + 3, y + 2);
  }

  doc.addPage();
}

function addFooter(doc: jsPDF): void {
  const pageCount = doc.getNumberOfPages();
  const generatedAt = new Date().toLocaleString();
  const w = doc.internal.pageSize.getWidth();
  const h = doc.internal.pageSize.getHeight();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(...TRIM);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, h - 12, w - MARGIN, h - 12);
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text('BetaNXT Ace · Confidential', MARGIN, h - 7);
    doc.text(`Generated ${generatedAt}`, w / 2, h - 7, { align: 'center' });
    doc.text(`Page ${i} of ${pageCount}`, w - MARGIN, h - 7, { align: 'right' });
  }
}

export async function exportOpsReportPdf(data: OpsReportData): Promise<void> {
  const { period, isMock, summary, breakdown, fieldAcc, weekScans, floorControl, auditTrail } = data;
  const doc = new jsPDF();

  await addCoverPage(doc, period, isMock);

  let y = 18;
  y = addSectionTitle(doc, 'Control Summary', y);
  y = addSubtitle(doc, 'Summarizes AI extraction accuracy controls in effect for the stated period.', y);
  y = addTable(doc, y, ['Metric', 'Value'], [
    ['Total Scans Processed', String(summary.total_receipts)],
    ['Overall Field Accuracy', `${summary.overall_avg_score_pct}%`],
    ['100% Accurate Scans', breakdown ? String(perfectScans(breakdown)) : '—'],
    ['Weakest Field', fieldAcc.length > 0 ? `${fieldAcc[0].label} (${fieldAcc[0].correct_pct}%)` : '—'],
  ]);

  if (breakdown) {
    y = addSectionTitle(doc, 'Accuracy by Category', y);
    y = addTable(doc, y, ['Category', 'Total Scans', 'Avg Accuracy', 'Perfect (100%)', 'Mid (80-99%)', 'Low (<80%)'], breakdownRows(breakdown), (d: any) => {
      if (d.section === 'body' && d.column.index === 2) {
        d.cell.styles.textColor = categoryAccuracyColor(parseFloat(d.cell.raw as string));
      }
    });
  }

  if (fieldAcc.length > 0) {
    y = addSectionTitle(doc, 'Field-Level Accuracy', y);
    y = addTable(doc, y, ['Field', 'Accuracy', 'Scans Analyzed'],
      fieldAcc.map(f => [f.label, `${f.correct_pct}%`, String(f.total)]), (d: any) => {
        if (d.section === 'body' && d.column.index === 1) {
          d.cell.styles.textColor = fieldAccuracyColor(parseFloat(d.cell.raw as string));
        }
      });
  }

  doc.addPage();
  y = 18;
  y = addSectionTitle(doc, 'Exception & Escalation Log', y);
  y = addSubtitle(doc, 'Evidence of exception handling and escalation activity for the stated period.', y);

  y = addCountTable(doc, y, 'Exceptions by Type', 'Status', floorControl.exceptionsByStatus, (d: any) => {
    if (d.section === 'body' && d.column.index === 0) {
      const label = d.cell.raw as string;
      if (label === 'No Job Found' || label === 'Received Short') d.cell.styles.textColor = DANGER_TEXT;
      else if (label === 'On Hold') d.cell.styles.textColor = OUTSTANDING_TEXT;
    }
  });
  y = addCountTable(doc, y, 'Escalations by Team', 'Team', floorControl.escalationsByTeam);
  y = addCountTable(doc, y, 'Intake Volume by Operator', 'Operator', floorControl.intakeByOperator);
  y = addCountTable(doc, y, 'Overs by Status', 'Status', floorControl.oversByStatus);

  if (auditTrail.length > 0) {
    doc.addPage();
    y = 18;
    y = addSectionTitle(doc, 'Audit Trail', y);
    y = addSubtitle(doc, `${auditTrail.length} logged events`, y);
    addTable(doc, y, ['Timestamp', 'Actor', 'Type', 'Scan #', 'Details'],
      auditTrail.map(a => [a.ts, a.actor, a.action, a.target, a.details]));
  }

  if (weekScans.length > 0) {
    doc.addPage();
    y = 18;
    y = addSectionTitle(doc, 'Appendix A: Scan-Level Detail', y);
    y = addSubtitle(doc, 'Detailed per-scan records for the selected period.', y);
    addTable(doc, y, ['Scan ID', 'Client', 'Date', 'Accuracy', 'Score', 'Issues'],
      weekScans.map(s => [
        s.scan_id, s.client_name, s.date, `${s.accuracy_pct}%`,
        `${s.fields_correct}/${s.fields_total}`,
        s.issues.length > 0 ? s.issues.join(', ') : '—',
      ]));
  }

  addFooter(doc);

  const slug = period.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'report';
  doc.save(`ops-report-${slug}.pdf`);
}
